import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'antd'

// Xem full ảnh/video, kéo/trượt theo ngón (như app camera) + chuyển VÒNG liền mạch.
// Kỹ thuật cửa sổ 3 slide: luôn render [prev, current, next] (tính vòng), track căn giữa slide
// current (-100%). Vuốt sang → animate tới slide kế (-200%/0%), xong thì TÁI CĂN ngầm (không
// animate) về -100% với index mới → wrap (cuối↔đầu) cũng trượt mượt như bước thường.
// items: [{ url, video }]. Overlay cố định toàn màn hình.
const MediaViewer = ({ items, index, onIndexChange, onClose }) => {
    const count = items.length
    const many = count > 1

    const [pos, setPos] = useState(-100)        // % vị trí track (chỉ dùng khi many)
    const [drag, setDrag] = useState(0)         // px kéo tay
    const [dragging, setDragging] = useState(false)
    const [noAnim, setNoAnim] = useState(false) // tắt transition lúc tái căn ngầm
    const startXRef = useRef(null)
    const widthRef = useRef(0)
    const stageRef = useRef(null)
    const animatingRef = useRef(false)

    const prevI = (index - 1 + count) % count
    const nextI = (index + 1) % count
    const windowSlides = many ? [items[prevI], items[index], items[nextI]] : [items[index]]

    const _recenter = (target) => {
        animatingRef.current = false
        setNoAnim(true)
        setPos(-100)
        setDrag(0)
        onIndexChange(target)
        setTimeout(() => setNoAnim(false), 30) // bật lại transition sau khi đã căn giữa
    }
    const _onTransitionEnd = () => {
        if (pos === -200) _recenter(nextI)
        else if (pos === 0) _recenter(prevI)
    }
    const _commit = (dir) => {
        if (!many || animatingRef.current) return
        animatingRef.current = true
        setDragging(false)
        setDrag(0)
        setPos(dir > 0 ? -200 : 0) // animate sang slide kế; transitionEnd sẽ tái căn
    }

    const _onTouchStart = (e) => {
        if (!many || animatingRef.current || !e.touches || e.touches.length !== 1) return
        startXRef.current = e.touches[0].clientX
        widthRef.current = (stageRef.current && stageRef.current.offsetWidth) || window.innerWidth
        setDragging(true)
    }
    const _onTouchMove = (e) => {
        if (startXRef.current == null) return
        setDrag(e.touches[0].clientX - startXRef.current)
    }
    const _onTouchEnd = () => {
        if (startXRef.current == null) return
        const dx = drag
        const threshold = Math.min(80, (widthRef.current || 1) * 0.2)
        startXRef.current = null
        setDragging(false)
        if (dx <= -threshold) _commit(1)
        else if (dx >= threshold) _commit(-1)
        else setDrag(0) // chưa đủ ngưỡng → trượt về chỗ cũ
    }

    const trackStyle = many
        ? {
            transform: `translateX(calc(${pos}% + ${drag}px))`,
            transition: (dragging || noAnim) ? 'none' : 'transform 0.25s ease',
        }
        : {}

    return (
        <div className="MediaViewer" onClick={onClose}>
            <div className="mv-bar">
                <span className="mv-count">{index + 1}/{count}</span>
                <Button size="small" ghost icon="close" onClick={onClose}>Đóng</Button>
            </div>
            <div
                className="mv-stage"
                ref={stageRef}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={_onTouchStart}
                onTouchMove={_onTouchMove}
                onTouchEnd={_onTouchEnd}
            >
                <div className="mv-track" style={trackStyle} onTransitionEnd={_onTransitionEnd}>
                    {windowSlides.map((it, slot) => (
                        // key theo VỊ TRÍ trong cửa sổ (0/1/2) — ổn định khi index đổi, tránh trùng key khi count=2
                        // eslint-disable-next-line react/no-array-index-key
                        <div className="mv-slide" key={slot}>
                            {it.video
                                ? <video src={it.url} controls preload="metadata" />
                                : <img src={it.url} alt="" draggable={false} />}
                        </div>
                    ))}
                </div>
                {many && (
                    <button type="button" className="mv-nav mv-prev" onClick={() => _commit(-1)} aria-label="Trước">‹</button>
                )}
                {many && (
                    <button type="button" className="mv-nav mv-next" onClick={() => _commit(1)} aria-label="Sau">›</button>
                )}
            </div>
        </div>
    )
}

MediaViewer.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string,
        video: PropTypes.bool,
    })).isRequired,
    index: PropTypes.number.isRequired,
    onIndexChange: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
}

export default MediaViewer
